<script setup>
import { useAppStore } from '~/store/main'
import { useActualitiesStore } from '~/store/actualities'
import noticesQuery from '~/assets/gql/notices.gql'
import homepageQuery from '~/assets/gql/homepage.gql'

const { t, locale } = useI18n()
const mainStore = useAppStore()
const { getIsDesktop } = storeToRefs(mainStore)
const actualitiesStore = useActualitiesStore()
const { getActualitiesToday, getActualitiesProgram, getActualitiesNotice } =
  storeToRefs(actualitiesStore)
const { setActualities } = actualitiesStore
const { throwFetchError, throwNotFound } = useThrowErrors()

const hoverToday = ref(false)
const hoverProgram = ref(false)
const hoverNotice = ref(false)

const {
  data: homepageData,
  error,
  status,
} = await useAsyncQuery(homepageQuery, {
  language: locale.value.toUpperCase(),
})

if (error.value) throwFetchError()

if (status.value === 'success' && !homepageData.value?.articles?.nodes?.[0])
  throwNotFound()

const data = computed(() => homepageData.value?.articles?.nodes?.[0])

const now = new Date()
now.setUTCHours(now.getUTCHours() - 2)
const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

const { result, loading: noticesLoading } = useQuery(noticesQuery, {
  languageNotices: locale.value.toUpperCase(),
  now: today,
})

watch(
  () => result.value,
  (newValue) => {
    if (newValue) {
      setActualities(newValue.notices.edges)
    }
  },
  { immediate: true }
)
</script>

<template>
  <div>
    <div>
      <Hero
        :img="data?.featuredImage || {}"
        :mobil-img="data?.featuredMobileImage?.featuredMobileImage || {}"
        :video="data?.HeroVideo?.url"
        logo="avu"
        :title="data?.title"
        :from-home-page="false"
        :link="data?.GlobalAttributes?.heroLink"
      />
      <Logo
        :logo-visibility="!!data.GlobalAttributes.displayHeroLogo"
        :logo-variant="data.GlobalAttributes.heroLogoColor"
      />
    </div>

    <div v-if="!getIsDesktop" class="homepageBlock homepageBlock--first">
      <MainCategories />
    </div>

    <div v-if="noticesLoading" class="loader">
      <Loader />
    </div>

    <div class="programWrapper">
      <div
        class="homepageBlock"
        :class="{ 'homepageBlock--opacity': hoverNotice || hoverProgram }"
        @mouseover="hoverToday = true"
        @mouseleave="hoverToday = false"
      >
        <Program
          v-show="getActualitiesToday.length"
          :program-title="t('notices.today')"
          :actualities="getActualitiesToday"
        />
      </div>
      <div
        class="homepageBlock"
        :class="{ 'homepageBlock--opacity': hoverToday || hoverNotice }"
        @mouseover="hoverProgram = true"
        @mouseleave="hoverProgram = false"
      >
        <Program
          v-show="getActualitiesProgram.length"
          :program-title="t('notices.program')"
          :actualities="getActualitiesProgram"
          :link-label="t('general.all-program')"
          link-name="program"
        />
      </div>
      <div
        v-show="getActualitiesNotice.length"
        class="homepageBlock"
        :class="{ 'homepageBlock--opacity': hoverToday || hoverProgram }"
        @mouseover="hoverNotice = true"
        @mouseleave="hoverNotice = false"
      >
        <Program
          :program-title="t('notices.notification')"
          :actualities="getActualitiesNotice.slice(0, 15)"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.homepageBlock {
  padding: 35.5px 0;
  transition: all 0.5s ease;

  &--first {
    padding-top: 90px;
    padding-bottom: 54.5px;
  }
}

.programWrapper {
  @include media-breakpoint-up(sm) {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: flex-start;
    gap: 40px;

    .homepageBlock {
      padding: 0;
      flex: 1;
    }
  }
}

.loader svg {
  position: relative;
  transform: translateX(-50%);
  width: max-content;
}
</style>
